import { ScrollKeys } from '../constants/Scroll';
import { document, window } from '../tools/Globals';

export const canWindowScrollUp = (): boolean => {
  return window.pageYOffset > 0;
};

export const canWindowScrollDown = (): boolean => {
  return window.pageYOffset < document.body.scrollHeight;
};

export const getScrollAmount = (): number => {
  return document.documentElement?.scrollTop || document.body.scrollTop || 0;
};

export function scrollToTop(distanceFromTop = 0): number {
  document.body.scrollTop = distanceFromTop;
  document.documentElement.scrollTop = distanceFromTop;
  return distanceFromTop;
}

/**
 * Gets the scroll offset from state
 *
 * @param {object} state - redux state
 * @returns {number} - the current scroll position
 */
export function getScrolledOffset(state: $TSFixMe): number {
  return state.scroll.get(ScrollKeys.SCROLLED_OFFSET);
}

/**
 * Class AutoScroller encapsulates auto-scrolling of a list when dragging list items.
 * This version does vertical scrolling only, and scrolls the window.
 * Scrolling animation is handled by an interval function called 10 times per second,
 * based on the Y position of the mouse.
 *
 * maxScrollRate - in pixels per second, defaults to 100
 * minScrollRate - in pixels per second, defaults to 10
 * scrollZoneHeight - height in pixels of active scroll zone from top and bottom of scrollingElement
 *  - defaults to 100
 * scrollZoneMaxRegionHeight - height in pixels of region at top of scroll zone where rate is maximum
 *  - defaults to 10
 */

export class AutoScroller {
  lastYPosition: number;
  maxScrollRate: number;
  minScrollRate: number;
  scrollFooterHeight: number;
  scrollHeaderHeight: number;
  scrollRate: number;
  scrollZoneHeight: number;
  scrollZoneMaxRegionHeight: number;
  scrollerFunction: ReturnType<typeof setInterval> | null;
  constructor(
    scrollZoneHeight = 75,
    scrollZoneMaxRegionHeight = 10,
    scrollFooterHeight = 64,
    scrollHeaderHeight = 64,
    maxScrollRate = 50,
    minScrollRate = 1,
  ) {
    this.scrollFooterHeight = scrollFooterHeight;
    this.scrollHeaderHeight = scrollHeaderHeight;
    this.maxScrollRate = maxScrollRate;
    this.minScrollRate = minScrollRate;
    this.scrollZoneHeight = scrollZoneHeight;
    this.scrollZoneMaxRegionHeight = scrollZoneMaxRegionHeight;

    this.lastYPosition = Number.MIN_SAFE_INTEGER;
    this.scrollerFunction = null;
    this.scrollRate = 0;
  }

  calculateScrollRate = (yPositionOffset: number): number => {
    const variableZoneHeight = this.scrollZoneHeight - this.scrollZoneMaxRegionHeight;
    const adjustedYPosOfs = yPositionOffset >= 0 ? yPositionOffset : 0;
    let resultRatio = 1.0;
    if (variableZoneHeight > 0 && adjustedYPosOfs < variableZoneHeight) {
      resultRatio = adjustedYPosOfs / variableZoneHeight;
    }
    return this.minScrollRate + (this.maxScrollRate - this.minScrollRate) * resultRatio;
  };

  doHandleScroll = (scrollRate: number): void => {
    if (this.scrollerFunction) {
      this.scrollRate = scrollRate;
    } else {
      this.startNewScroll(scrollRate);
    }
  };

  endScroll = (): void => {
    if (this.scrollerFunction) {
      clearInterval(this.scrollerFunction);
      this.scrollerFunction = null;
    }
  };

  handleHover = (yPosition: number): void => {
    if (yPosition !== this.lastYPosition) {
      if (this.inScrollDownRegion(yPosition)) {
        this.handleScrollDown(yPosition);
      } else if (this.inScrollUpRegion(yPosition)) {
        this.handleScrollUp(yPosition);
      } else {
        this.endScroll();
      }
      this.lastYPosition = yPosition;
    }
  };

  handleScrollDown = (yPosition: number): void => {
    if (canWindowScrollDown()) {
      this.doHandleScroll(
        this.calculateScrollRate(
          this.scrollZoneHeight - (window.innerHeight - this.scrollHeaderHeight - yPosition),
        ),
      );
    }
  };

  handleScrollUp = (yPosition: number): void => {
    if (canWindowScrollUp()) {
      this.doHandleScroll(
        -this.calculateScrollRate(this.scrollZoneHeight + this.scrollFooterHeight - yPosition),
      );
    }
  };

  inScrollDownRegion = (yPosition: number): boolean => {
    return yPosition >= window.innerHeight - this.scrollZoneHeight - this.scrollHeaderHeight;
  };

  inScrollUpRegion = (yPosition: number): boolean => {
    return yPosition <= this.scrollZoneHeight + this.scrollFooterHeight;
  };

  startNewScroll = (scrollRate: number): void => {
    this.scrollRate = scrollRate;
    this.scrollerFunction = setInterval(() => {
      if ((scrollRate < 0 && canWindowScrollUp()) || (scrollRate > 0 && canWindowScrollDown())) {
        window.scrollBy(0, this.scrollRate);
      } else {
        this.endScroll();
      }
    }, 10);
  };
}
